<template>
  <v-form ref="form" v-model="valid" @submit="register">
    <div>
      <v-container class="pt-0">
        <span class="headline">{{$t('registration.title')}}</span>
        <v-row v-if="successMessage">
          <v-col>
            <alert :text="successMessage" :type="'success'" />
          </v-col>
        </v-row>
        <v-row v-if="loadingForm">
          <v-col class="pb-3 ma-0">
            <v-progress-linear :indeterminate="true" :height="2"></v-progress-linear>
          </v-col>
        </v-row>
        <div v-else>
          <v-row no-gutters v-if="formContains('name')">
            <v-col>
              <v-text-field
                data-test-id="name"
                v-model="name"
                :disabled="loading"
                :rules="nameRules"
                :label="$t('common.nickname')"
                autocomplete="off"
                prepend-icon="mdi-account"
                :append-icon="nameAvailable == true ? 'mdi-check' : ''"
                required
                v-debounce:700="checkUsername"
                validate-on-blur
              ></v-text-field>
            </v-col>
            <v-col cols="12" class="ml-8 mb-2 pt-0" v-if="usernameError" no-gutters>
              <info-block icon="mdi-alert" style="color: var(--v-error-base);"><span>{{usernameError}}</span></info-block>
            </v-col>
          </v-row>
          <v-row no-gutters v-if="formContains('sex')">
            <v-col class="ml-6">
              <v-radio-group
                v-model="sex"
                :disabled="loading"
                :rules="formRequires('sex') ? requiredRule : []"
                row
              >
                <v-radio
                  :label="$t('profile.salutation.m')"
                  value="m"
                ></v-radio>
                <v-radio
                  :label="$t('profile.salutation.f')"
                  value="f"
                ></v-radio>
              </v-radio-group>
            </v-col>
          </v-row>
          <v-row no-gutters v-if="formContains('firstname')">
            <v-col>
              <v-text-field
                data-test-id="firstname"
                v-model="firstname"
                :disabled="loading"
                name="firstname"
                prepend-icon="mdi-pencil"
                autocomplete="given-name"
                :label="$t('profile.firstname')"
                :rules="formRequires('firstname') ? requiredRule : []"
                :required="formRequires('firstname')"
                validate-on-blur
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row no-gutters v-if="formContains('lastname')">
            <v-col>
              <v-text-field
                data-test-id="lastname"
                v-model="lastname"
                :disabled="loading"
                name="lastname"
                prepend-icon="mdi-pencil"
                autocomplete="family-name"
                :label="$t('profile.lastname')"
                :rules="formRequires('lastname') ? requiredRule : []"
                :required="formRequires('lastname')"
                validate-on-blur
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row no-gutters v-if="formContains('year_of_birth')">
            <v-col>
              <year-select
                v-model="year_of_birth"
                :label="$t('profile.year_of_birth')"
                :disabled="loading"
                prepend-icon="mdi-pencil"
                validate-on-blur
                autocomplete="bday-year"
                :rules="formRequires('year_of_birth') ? requiredRule : []"
              />
            </v-col>
          </v-row>
          <v-row no-gutters v-if="formContains('email')">
            <v-col>
              <v-text-field
                data-test-id="email"
                v-model="email"
                :disabled="loading"
                :rules="emailRules"
                name="email"
                autocomplete="username"
                :label="$t('common.email-address')"
                prepend-icon="mdi-at"
                required
                validate-on-blur
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row no-gutters v-if="formContains('password')">
            <v-col>
              <v-text-field
                data-test-id="password"
                v-model="password"
                :disabled="loading"
                id="password"
                :label="$t('common.password')"
                autocomplete="new-password"
                :rules="passwordRules"
                name="password"
                :hint="$t('common.password-hint', {n: 8})"
                prepend-icon="mdi-lock"
                type="password"
                validate-on-blur
              />
            </v-col>
          </v-row>
          <v-row no-gutters v-if="formContains('password')">
            <v-col>
              <v-text-field
                data-test-id="confirmPassword"
                v-model="confirmPassword"
                :disabled="loading"
                :label="$t('common.confirm-password')"
                autocomplete="new-password"
                :hint="$t('common.confirm-password-hint')"
                :rules="passwordRules"
                name="confirmPassword"
                prepend-icon="mdi-lock"
                type="password"
                validate-on-blur
              />
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col>
              <v-checkbox
                data-test-id="iam16minimum"
                v-model="minimum16yrsold"
                :disabled="loading"
                :rules="[v => !!v || $t('registration.i-am-min-16-years-old-required')]"
                :label="$t('registration.i-am-min-16-years-old')"
                required
              ></v-checkbox>
            </v-col>
          </v-row>

          <v-row no-gutters>
            <v-col cols="12" sm="6" class="py-1">
              <v-btn @click="openAGBs" class="secondary darken-3">
                <v-icon left>mdi-open-in-app</v-icon>{{$t('registration.show-legals')}}
              </v-btn>
              <aGBDialog
                :visible="showDialog.agb"
                :closeAction="function() {showDialog.agb = false;}"
              />
            </v-col>
            <v-col cols="12" sm="6" class="py-1">
              <v-btn
                @click="function() {showDialog.privacyPolicy = true;}"
                class="secondary darken-3"
              >
                <v-icon left>mdi-open-in-app</v-icon>{{$t('registration.show-privacy-policy')}}
              </v-btn>
              <privacy-policy-dialog
                :visible="showDialog.privacyPolicy"
                :closeAction="function() {showDialog.privacyPolicy = false;}"
              />
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col>
              <v-checkbox
                data-test-id="acceptLegals"
                v-model="accept"
                :disabled="loading"
                :rules="[v => !!v || $t('registration.pp-and-legals-required')]"
                :label="$t('registration.i-accept-privacy-policy-and-legals')"
                required
              ></v-checkbox>
            </v-col>
          </v-row>
          <v-checkbox
              data-test-id="acceptNewsletter"
              v-model="newsletter"
              :disabled="loading"
              :label="$t('registration.i-subscribe-newsletter')"
          ></v-checkbox>
          <v-row no-gutters>
            <v-col>
              <v-btn
                data-test-id="submitButton"
                type="submit"
                color="primary"
                :disabled="!valid || !filled || loading"
                :loading="loading"
              >
                <v-icon left>mdi-send</v-icon>{{$t('registration.sign-up')}}
              </v-btn>
            </v-col>
            <v-col>
              <v-btn class="mr-4" @click="reset" :disabled="loading" data-test-id="resetButton">
                <v-icon left>mdi-delete</v-icon>{{$t('registration.reset')}}
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </v-container>
    </div>
  </v-form>
</template>

<script>
import AGBDialog from "@/widgets/AGBDialog";
import PrivacyPolicyDialog from "@/widgets/PrivacyPolicyDialog";
import Alert from "@/widgets/Alert";
import {
  MUTATION_REGISTERED,
  MUTATION_RAISE_NOTIFICATION,
  MUTATION_DISMISS_NOTIFICATION
} from "@/plugins/store.js";
import InfoBlock from '@/widgets/InfoBlock.vue';
import YearSelect from '../widgets/YearSelect.vue';

export default {
  name: "Register",
  components: {
    AGBDialog,
    PrivacyPolicyDialog,
    Alert,
    InfoBlock,
    YearSelect
  },
  data: function() {
    return {
      loadingForm: false,
      formInfo: undefined,
      valid: true,
      showDialog: {
        agb: false,
        privacyPolicy: false
      },
      successMessage: undefined,
      loading: false,
      usernameError: undefined,
      name: "",
      nameAvailable: undefined,
      nameRules: [
        v => !!v || this.$t('registration.nickname-required'),
        v =>
          (v && v.length < 100) ||
          this.$t('registration.nickname-max-chars', {n: 100}),
        v =>
          (v && v.indexOf(" ") == -1) ||
          this.$t('registration.nickname-no-spaces'),
        v =>
          (v && v.length >= 1) ||
          this.$t('registration.nickname-min-chars', {n: 1}),
      ],
      firstname: undefined,
      lastname: undefined,
      sex: undefined,
      year_of_birth: undefined,
      email: "",
      emailRules: [
        v => !!v || this.$t('common.email-address-required'),
        v => /.+@.+\..+/.test(v) || this.$t('common.e-mail-address-has-to-be-valid')
      ],
      accept: false,
      newsletter: false,
      minimum16yrsold: false,
      password: "",
      passwordRules: [
        v => !!v || this.$t('common.password-required'),
        v =>
          (v && v.length >= 8) ||
          this.$t('common.password-min-n-length', {n: 8})
      ],
      confirmPassword: "",
      requiredRule: [
        v => !!v || this.$t('common.mandatory-field'),
      ]
    }
  },

  computed: {
    filled() {
      return (
        this.name &&
        this.name.length > 0 &&
        this.email &&
        this.email.length > 0 &&
        this.password &&
        this.password.length > 0 &&
        this.confirmPassword &&
        this.confirmPassword.length > 0
      );
    }
  },

  mounted: function() {
    this.loadingForm = true
    this.$api.getRegistrationForm()
    .then(res => {
      this.loadingForm = false
      this.formInfo = res.data
    })
    .catch(error => {
      this.loadingForm = false
      this.$common.onError(error)
    });
  },

  methods: {
    register(e) {
      e.preventDefault();
      this.validate();
      if (this.password != this.confirmPassword) {
        this.$store.commit(MUTATION_RAISE_NOTIFICATION, {
          type: "error",
          message: this.$t('registration.passwords-do-not-match')
        });
        this.valid = false;
      }
      if (!this.valid) {
        return;
      }
      this.successMessage = null;
      this.loading = true;
      this.$store.commit(MUTATION_DISMISS_NOTIFICATION);
      console.log("Register...");
      let data = {
          name: this.name,
          email: this.email,
          password: this.password,
          password_confirmation: this.confirmPassword,
          redirect: this.$route.query.redirect,
          is_subscriber: this.newsletter
        }
      if (this.formContains('firstname')) {
        data.firstname = this.firstname
      }
      if (this.formContains('lastname')) {
        data.lastname = this.lastname
      }
      if (this.formContains('sex')) {
        data.sex = this.sex
      }
      if (this.formContains('year_of_birth')) {
        data.year_of_birth = this.year_of_birth
      }
      this.$http
        .post("/api/user/register", data)
        .then(response => {
          this.loading = false;
          console.log(response);
          this.$store.commit(MUTATION_REGISTERED, {
            email: this.email,
            name: this.name
          });
          this.reset();
          let msg = this.$t('registration.registration-success-message');
          this.successMessage = msg;
          this.$ga.send({
            hitType: "event",
            eventCategory: "User",
            eventAction: "registered"
          });
        })
        .catch(error => {
          this.loading = false;
          this.$common.onError(error)
        });
      return false;
    },
    validate() {
      this.$refs.form.validate();
    },
    getFormField(fieldname) {
      if (! this.formInfo) return undefined
      let ret = this.formInfo.fields.find((field) => {
        return field.name == fieldname
      })
      return ret
    },
    formContains(fieldname) {
      if (! this.formInfo) return false
      return this.getFormField(fieldname) !== undefined
    },
    formRequires(fieldname) {
      let field = this.getFormField(fieldname)
      return field && field.optional == false
    },
    checkUsername() {
      console.log("checkUsername");
      this.nameAvailable = undefined
      if (this.name && this.name.trim().length > 0) {
        let checkedName = this.name
        this.$api
          .checkUsernameFree(checkedName)
          .then(() => {
            // Free
            this.usernameError = undefined
            if (this.name == checkedName) {
              this.nameAvailable = true
            }
          })
          .catch(error => {
            this.nameAvailable = false
            if (error.response && error.response.data && error.response.data.errors && error.response.data.errors.name) {
              this.usernameError = error.response.data.errors.name[0]
            } else if (
              error.response &&
              error.response.data &&
              error.response.data.message
            ) {
              this.usernameError = error.response.data.message;
            } else {
              this.usernameError = "" + error;
            }
          });
      }
    },
    resetUsernameError() {
      this.usernameError = undefined;
      this.$store.commit(MUTATION_DISMISS_NOTIFICATION);
    },
    reset() {
      this.$refs.form.reset();
      this.usernameError = undefined
      this.successMessage = undefined
      this.nameAvailable = undefined
    },
    resetValidation() {
      this.$refs.form.resetValidation();
    },
    openAGBs() {
      this.showDialog.agb = true;
    }
  }
};
</script>

<style scoped>
.registerCol {
  padding-top: 0;
  padding-bottom: 0;
}
</style>
